import {
  PageHeader,
  message,
  Modal,
  Form,
  Input,
  Select,
  Button as AntButton,
} from 'antd'
import TableBlock from '../components/tables/TableBlock'
import { getEvents, updateEvent, createEvent } from '../network/Events'
import { getClients } from '../network/Clients'
import { Badge, Space, Button } from 'antd'
import EditableCell from '../components/EditableCell'
import { useState } from 'react'
import { useQueryClient, useQuery } from 'react-query'
import moment from 'moment'
import { ROLE_PERMISSIONS } from '../constants/common'
import { useAuth } from '../hooks/useAuth'
import useIsMobile from '../hooks/useIsMobile'

const Crm = ({ history }) => {
  const queryClient = useQueryClient()
  const [editingKey, setEditingKey] = useState('')
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [isEventDetailModalVisible, setIsEventDetailModalVisible] =
    useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [form] = Form.useForm()
  const isMobile = useIsMobile()

  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []

  const { data: clientsData } = useQuery(
    ['getClients', { sort: [], range: [0, 1000], filter: {}, tabFilter: {} }],
    getClients,
    {
      enabled: isCreateModalVisible,
    }
  )

  const handleComplete = (record) => {
    Modal.confirm({
      title: 'Confirm Complete Event',
      content: `Are you sure you want to mark this event as completed for ${record.customer?.firstName} ${record.customer?.lastName}?`,
      okText: 'Yes, Complete',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await updateEvent(record.id, { status: 'handled' })
          message.success('Event status updated to completed')
          queryClient.invalidateQueries(['getEvents'])
        } catch (error) {
          message.error('Failed to update event status')
          console.error('Error updating status:', error)
        }
      },
    })
  }

  const isEditing = (record) => {
    const isCurrentlyEditing = record.id === editingKey
    return isCurrentlyEditing
  }

  const edit = (id) => {
    setEditingKey(id)
  }

  const cancel = () => {
    setEditingKey('')
  }

  const save = async (id, data) => {
    try {
      await updateEvent(id, data)
      message.success('Notes updated successfully')
      queryClient.invalidateQueries(['getEvents'])
    } catch (error) {
      message.error('Failed to update notes')
      console.error('Error updating notes:', error)
    }
  }

  const handleCreateEvent = async (values) => {
    try {
      const eventData = {
        customer: values.customer,
        eventDate: moment().format('YYYY-MM-DD'),
        notes: values.notes || '',
        createdBy: user.id,
        eventType: values.eventType || 'manual',
      }
      await createEvent(eventData)
      message.success('Event created successfully')
      queryClient.invalidateQueries(['getEvents'])
      setIsCreateModalVisible(false)
      form.resetFields()
    } catch (error) {
      message.error('Failed to create event')
      console.error('Error creating event:', error)
    }
  }

  const showCreateModal = () => {
    setIsCreateModalVisible(true)
  }

  const handleCancelCreate = () => {
    setIsCreateModalVisible(false)
    form.resetFields()
  }

  let filters = (history) => ({
    fields: [
      {
        type: 'select',
        label: 'Event',
        key: 'eventType',
        noRegex: true,
        options: [
          { label: 'Birthday', key: 'birthday' },
          { label: '6M follow up', key: 'purchase_followup_6m' },
          { label: '1Y follow up', key: 'purchase_followup_1y' },
          { label: '2Y follow up', key: 'purchase_followup_2y' },
          { label: 'Partner', key: 'partner' },
          { label: ' Free shirt', key: 'free_shirt' },
          {
            label: ' New orders needed of existing client',
            key: 'new_orders_needed_of_existing_client',
          },
          { label: 'Manual', key: 'manual' },
        ],
      },
      {
        type: 'select',
        label: 'Status',
        key: 'status',
        noRegex: true,
        options: [
          { label: 'Pending', key: 'pending' },
          { label: 'Handled', key: 'handled' },
          { label: 'Ignored', key: 'ignored' },
          { label: 'Expired', key: 'expired' },
        ],
      },
      {
        type: 'text',
        label: 'Customer',
        key: 'customer.name',
        noRegex: true,
      },
    ],
    actions: [
      {
        label: '+ Create an event',
        isActive: permissions.includes('can-create-events'),
        action: showCreateModal,
      },
    ],
  })

  const getRowClassName = (record) => {
    const status = record.status?.toLowerCase()
    switch (status) {
      case 'pending':
        return 'status-pending'
      case 'handled':
        return 'status-handled'
      case 'ignored':
        return 'status-ignored'
      case 'expired':
        return 'status-expired'
      default:
        return ''
    }
  }

  const columns = (history) => [
    {
      title: 'Event',
      key: 'eventType',
      dataIndex: 'eventType',
      className: 'crm-event-col',
      ellipsis: true,
    },
    {
      title: 'Customer',
      key: 'customer',
      dataIndex: 'customer',
      render: (e) =>
        e ? (
          <a
            href={`/clients/${e?.id}/view`}
          >{`${e?.firstName} ${e?.lastName}`}</a>
        ) : (
          '-'
        ),
    },
    ...(!isMobile
      ? [
          {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            render: (status) => {
              const statuses = {
                pending: { text: 'Pending', color: 'green' },
                expired: { text: 'Expired', color: 'red' },
                ignored: { text: 'Ignored', color: 'gray' },
                handled: { text: 'Handled', color: 'blue' },
              }

              return (
                <Badge
                  color={statuses[status].color}
                  text={statuses[status].text}
                />
              )
            },
            sorter: true,
          },
        ]
      : []),
        {
          title: 'Date',
          key: 'eventDate',
          dataIndex: 'eventDate',
          render: (text) => moment(text).format('YYYY-MM-DD'),
        },
        ...(!isMobile
            ? [
          {
            title: 'Created By',
            key: 'createdBy',
            dataIndex: 'createdBy',
            render: (createdBy) => createdBy?.name || 'auto',
          },
          {
            title: 'Notes',
            key: 'notes',
            dataIndex: 'notes',
            render: (text, record) => (
              <EditableCell
                value={text}
                record={record}
                dataIndex="notes"
                editingKey={editingKey}
                isEditing={isEditing(record)}
                onEdit={edit}
                onCancel={cancel}
                onSave={save}
              />
            ),
          },
          {
            title: 'Action',
            key: 'action',
            dataIndex: 'action',
            render: (_, record) => {
              const isCompleted = record.status?.toLowerCase() === 'handled'
              const isIgnored = record.status?.toLowerCase() === 'ignored'

              return (
                <>
                  {!isCompleted && !isIgnored && (
                    <Space>
                      {/* <Button onClick={() => handleIgnore(record)} type="danger">
                        Ignore
                      </Button> */}
                      <Button
                        onClick={() => handleComplete(record)}
                        type="primary"
                      >
                        Complete
                      </Button>
                    </Space>
                  )}
                </>
              )
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader className="site-page-header" title="CRM" />
      <div className="crm-wrapper">
        <TableBlock
          columns={columns}
          filters={filters}
          name={'getEvents'}
          request={getEvents}
          pageSize={100}
          onRow={(record) => ({
            className: getRowClassName(record),
            ...(isMobile && {
              onClick: () => {
                setSelectedEvent(record)
                setIsEventDetailModalVisible(true)
              },
              style: { cursor: 'pointer' },
            }),
          })}
        />
      </div>

      <Modal
        title="Event details"
        open={isEventDetailModalVisible}
        onCancel={() => {
          setIsEventDetailModalVisible(false)
          setSelectedEvent(null)
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsEventDetailModalVisible(false)
              setSelectedEvent(null)
            }}
          >
            Close
          </Button>,
          selectedEvent &&
            selectedEvent.status?.toLowerCase() !== 'handled' &&
            selectedEvent.status?.toLowerCase() !== 'ignored' && (
              <Button
                key="complete"
                type="primary"
                onClick={() => {
                  handleComplete(selectedEvent)
                  setIsEventDetailModalVisible(false)
                }}
              >
                Complete
              </Button>
            ),
        ].filter(Boolean)}
      >
        {selectedEvent && (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div>
              <strong>Event:</strong> {selectedEvent.eventType || '-'}
            </div>
            <div>
              <strong>Status:</strong> {selectedEvent.status || '-'}
            </div>
            <div>
              <strong>Customer:</strong>{' '}
              {selectedEvent.customer
                ? `${selectedEvent.customer.firstName || ''} ${selectedEvent.customer.lastName || ''}`.trim()
                : '-'}
            </div>
            <div>
              <strong>Date:</strong>{' '}
              {selectedEvent.eventDate
                ? moment(selectedEvent.eventDate).format('YYYY-MM-DD')
                : '-'}
            </div>
            <div>
              <strong>Created By:</strong> {selectedEvent.createdBy?.name || 'auto'}
            </div>
            <div>
              <strong>Notes:</strong> {selectedEvent.notes || '-'}
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        title="Create New Event"
        open={isCreateModalVisible}
        onCancel={handleCancelCreate}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEvent}
          initialValues={{ eventType: 'manual' }}
        >
          <Form.Item
            label="Customer"
            name="customer"
            rules={[{ required: true, message: 'Please select a customer' }]}
          >
            <Select
              showSearch
              placeholder="Select a customer"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={!clientsData}
            >
              {clientsData?.list?.map((client) => (
                <Select.Option key={client.id} value={client.id}>
                  {`${client.firstName} ${client.lastName}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Event Type"
            name="eventType"
            rules={[{ required: true, message: 'Please select an event type' }]}
          >
            <Select placeholder="Select event type">
              <Select.Option value="manual">Manual</Select.Option>
              <Select.Option value="partner">Partner</Select.Option>
              <Select.Option value="free_shirt">Free Shirt</Select.Option>
              <Select.Option value="new_orders_needed_of_existing_client">
                New orders needed of existing client
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              rows={4}
              placeholder="Optional notes for this event"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <AntButton type="primary" htmlType="submit">
                Create Event
              </AntButton>
              <AntButton onClick={handleCancelCreate}>Cancel</AntButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Crm
